import React, { useCallback, useEffect, useMemo } from 'react'
import { RouteProps, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'

import UserDeclarationsTemplate, {
  UserDeclarationsTemplateProps,
} from '../../templates/UserDeclarations'
import { useHeader } from '../../relay/Header/useHeader'
import { useFooter } from '../../relay/Footer/useFooter'
import { ActionButtonLook, ActionButtonVariant } from '../../components/ActionButton/styled'
// import { Icons } from '../../components/Icon'
import { actions, selectors } from '../../redux'
import { routesPath } from '../../router'
import { fullDeclarationToDeclaration, generateBlock } from '../../helpers/DeclarationHelpers'
import { useUser } from '../../relay/User/useUser'
import { FullDeclaration, Partner } from '../../types'

const UserDeclarationsPage: React.FC<RouteProps> = () => {
  const header = useHeader()
  const footer = useFooter()
  const { t } = useTranslation()
  const { user } = useUser()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const declaration = useSelector(selectors.declaration.formValues)
  const familyRelationships = useSelector(selectors.declaration.familyRelationships)

  useEffect(() => {
    if (!user?.cgu_accepted_at) {
      navigate(routesPath.home)
    }
  }, [user, navigate])

  useEffect(() => {
    dispatch(actions.declaration.setIsUpdatingDeclaration(false))
    dispatch(actions.declaration.initFormValues())
  }, [dispatch, user])

  useEffect(() => {
    return () => {
      dispatch(actions.declaration.resetPreviousSteps())
    }
  }, [dispatch])

  const saveDeclaration = useCallback(() => {
    dispatch(actions.declaration.saveDeclarationRequest())
  }, [dispatch])

  const handleDeletePartner = useCallback(() => {
    dispatch(actions.declaration.setFormValues({ name: 'partner', value: null }))
    saveDeclaration()
  }, [saveDeclaration, dispatch])

  const handleModifyPartner = useCallback(() => {
    dispatch(actions.declaration.initFormValues())
    dispatch(actions.declaration.setIsUpdatingDeclaration(true))
    dispatch(actions.declaration.setIsInDeclarationProcess(true))
    dispatch(actions.declaration.setStep(5))
    navigate(routesPath.declaration)
  }, [dispatch, navigate])

  const hasEmptySiren = useMemo(() => {
    const regexSiren = /^\d{9}$/
    const regexRnaOrSiren = /^(?:\d{9}|W\d{9})$/

    const checkItems = (items: FullDeclaration[]) =>
      items.some((item) => {
        if (item.type === 'FOREIGN_COMPANY') {
          return false // Pas de vérification pour les sociétés étrangères
        }

        if (item.type === 'MANDATE') {
          return (
            typeof item.siren === 'undefined' ||
            !regexRnaOrSiren.test(item.siren) ||
            item.siren === '' ||
            item.siren === null
          )
        }

        return (
          typeof item.siren === 'undefined' ||
          !regexSiren.test(item.siren) ||
          item.siren === '' ||
          item.siren === null
        )
      })

    const checkPartner = (partner?: Partner) => {
      if (!partner?.items) return false
      return checkItems(partner.items)
    }

    const checkChildren = (children?: Partner[]) => {
      if (!children || !Array.isArray(children)) return false
      return children.some((child) => checkItems(child.items || []))
    }

    const result =
      checkItems(declaration.items || []) ||
      checkPartner(declaration.partner) ||
      checkChildren(declaration.children)

    console.log('Result of hasEmptySiren:', result)
    return result
  }, [declaration])

  const templateProps: UserDeclarationsTemplateProps = useMemo(
    () => ({
      layout: {
        header,
        footer,
      },
      userInfo: {
        companyJobLabel: t('userDeclarations.userInfo.companyJobLabel'),
        companyJob: user?.declaration?.function?.name ?? '',
        createdAtLabel: t('userDeclarations.userInfo.createdAtLabel'),
        createdAt: format(
          user?.declaration?.first_created_at
            ? new Date(user.declaration.first_created_at)
            : new Date(),
          'dd/MM/yyyy'
        ),
        updatedAtLabel: t('userDeclarations.userInfo.updatedAtLabel'),
        updatedAt: format(
          user?.declaration?.updated_at ? new Date(user.declaration.updated_at) : new Date(),
          'dd/MM/yyyy'
        ),
      },
      actions: [
        {
          text: t('userDeclarations.actions.modify'),
          look: ActionButtonLook.outlined,
          variant: ActionButtonVariant.primary,
          iconPosition: 'right',
          onClick: () => {
            dispatch(actions.declaration.initFormValues())
            dispatch(actions.declaration.setIsUpdatingDeclaration(true))
            dispatch(actions.declaration.setIsInDeclarationProcess(true))
            dispatch(actions.declaration.setStep(1))
            navigate(routesPath.declaration)
          },
        },
        {
          text: t('declaration.form.summary.submit.label'),
          variant: ActionButtonVariant.primary,
          disabled: hasEmptySiren,
          onClick: () => {
            if (!hasEmptySiren) {
              dispatch(actions.declaration.setIsUpdatingDeclaration(true))
              dispatch(actions.declaration.setIsInDeclarationProcess(true))
              dispatch(actions.declaration.setStep(20))
              navigate(routesPath.declaration)
            }
          },
        },
      ],
      banner: {
        title: t('userDeclarations.banner.title'),
        headerButton: {
          text: t('userDeclarations.banner.headerButton.text'),
          variant: ActionButtonVariant.white,
          look: ActionButtonLook.outlined,
          iconProps: {
            icon: 'arrowLeft',
          },
          iconPosition: 'left',
          onClick: () => {
            navigate(-1)
          },
        },
        headerFaqButton: {
          text: 'FAQ',
          variant: ActionButtonVariant.white,
          look: ActionButtonLook.outlined,
          onClick: () => {
            navigate(routesPath.faq)
          },
        },
      },
      error: hasEmptySiren ? t('declaration.form.confirmation.siren') : undefined,
      declarationBlocks: [
        ...(declaration?.items?.length
          ? [
              generateBlock(
                t,
                {
                  title: t('userDeclarations.declarationBlocks.title.declarant', {
                    name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
                  }),
                  items: declaration.items.map((item) => ({
                    ...fullDeclarationToDeclaration(item),
                  })),
                },
                'declarant'
              ),
            ]
          : !declaration?.items?.length
            ? [
                generateBlock(
                  t,
                  {
                    title: t('userDeclarations.declarationBlocks.title.declarant', {
                      name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
                    }),
                    items: [],
                  },
                  'declarant'
                ),
              ]
            : []),
        ...(declaration?.partner?.items?.length
          ? [
              generateBlock(
                t,
                {
                  title: t('userDeclarations.declarationBlocks.title.partner', {
                    name: `${declaration.partner?.firstName ?? ''} ${
                      declaration.partner?.lastName ?? ''
                    }`,
                  }),
                  actions: {
                    onDelete: handleDeletePartner,
                    onModify: handleModifyPartner,
                  },
                  items: declaration.partner.items.map((item) => ({
                    ...fullDeclarationToDeclaration(item),
                  })),
                },
                'partner'
              ),
            ]
          : !declaration?.partner?.items?.length
            ? [
                generateBlock(
                  t,
                  {
                    title: t('declaration.form.declarationListWithoutName.label', {
                      type: t(`declaration.partner`),
                    }),
                    items: [],
                  },
                  'partner'
                ),
              ]
            : []),

        ...(declaration?.children?.length
          ? declaration.children.map((child) =>
              generateBlock(
                t,
                {
                  title: t('userDeclarations.declarationBlocks.title.familyMember', {
                    name: `${child?.firstName ?? ''} ${child?.lastName ?? ''}`,
                    relationship:
                      child?.familyRelationshipCustom ||
                      familyRelationships.find((fr) => fr.id === child?.familyRelationshipId)
                        ?.name ||
                      t('declaration.familyMember'),
                  }),

                  items:
                    child?.items?.map((item) => ({
                      ...fullDeclarationToDeclaration(item),
                    })) ?? [],
                },
                'child'
              )
            )
          : !declaration?.children?.length
            ? [
                generateBlock(
                  t,
                  {
                    title: t('declaration.form.declarationListWithoutName.label', {
                      type: t(`declaration.familyMember`),
                    }),
                    items: [],
                  },
                  'child'
                ),
              ]
            : []),
      ],
    }),
    [
      footer,
      header,
      t,
      hasEmptySiren,
      declaration,
      familyRelationships,
      dispatch,
      navigate,
      user,
      handleDeletePartner,
      handleModifyPartner,
    ]
  )

  return <UserDeclarationsTemplate {...templateProps} />
}

export default UserDeclarationsPage
